import type { CSSProperties } from 'react';
import { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { clsx } from 'clsx';
import { Icon } from '@/components/icons';

export type PaginationProps = {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  pageSize: number;
  onPageSizeChange: (size: number) => void;
  totalItems: number;
  className?: string;
  style?: CSSProperties;
};

const PAGE_NUMBER_CLASS =
  'flex h-[23px] min-w-7 items-center justify-center px-1 py-0.5 text-base leading-6';

const PAGE_SIZE_SELECT_CLASS =
  'h-10 w-[4.5rem] rounded border border-unilink-border-outline bg-white px-2.5 text-base text-unilink-text-primary focus:outline-none focus:ring-2 focus:ring-blue-navbar';

function buildVisiblePages(currentPage: number, totalPages: number): (number | '...')[] {
  const delta = 2;
  const range: number[] = [];
  const rangeWithDots: (number | '...')[] = [];

  const safeTotalPages = Math.max(1, totalPages);
  const safeCurrentPage = Math.max(1, Math.min(currentPage, safeTotalPages));

  for (
    let i = Math.max(2, safeCurrentPage - delta);
    i <= Math.min(safeTotalPages - 1, safeCurrentPage + delta);
    i += 1
  ) {
    range.push(i);
  }

  if (safeCurrentPage - delta > 2) {
    rangeWithDots.push(1, '...');
  } else {
    rangeWithDots.push(1);
  }

  rangeWithDots.push(...range);

  if (safeCurrentPage + delta < safeTotalPages - 1) {
    rangeWithDots.push('...', safeTotalPages);
  } else if (safeTotalPages > 1) {
    rangeWithDots.push(safeTotalPages);
  }

  return rangeWithDots;
}

export function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  pageSize,
  onPageSizeChange,
  totalItems,
  className,
  style,
}: PaginationProps) {
  const { t } = useTranslation();

  const visiblePages = useMemo(
    () => buildVisiblePages(currentPage, totalPages),
    [currentPage, totalPages]
  );

  if (totalItems === 0 || totalPages <= 0) {
    return null;
  }

  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);

  return (
    <div
      className={clsx(
        'flex flex-wrap items-center justify-between gap-4 px-5 py-6',
        className
      )}
      style={style}
    >
      <div className="flex items-center">
        <button
          type="button"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="flex items-center gap-1 text-base text-blue-navbar disabled:cursor-not-allowed disabled:opacity-40"
          aria-label={t('pagination.previous')}
        >
          <Icon icon="chevron-left" className="size-6" />
          <span>{t('pagination.previous')}</span>
        </button>

        <div className="flex items-center">
          {visiblePages.map((page, index) => (
            <Fragment key={`${page}-${index}`}>
              {page === '...' ? (
                <span className={clsx(PAGE_NUMBER_CLASS, 'font-normal text-unilink-text-primary')}>...</span>
              ) : (
                <button
                  type="button"
                  onClick={() => onPageChange(page)}
                  className={clsx(
                    PAGE_NUMBER_CLASS,
                    currentPage === page
                      ? 'font-semibold text-unilink-text-primary'
                      : 'font-normal text-blue-navbar'
                  )}
                >
                  {page}
                </button>
              )}
            </Fragment>
          ))}
        </div>

        <button
          type="button"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="flex items-center gap-1 text-base text-blue-navbar disabled:cursor-not-allowed disabled:opacity-40"
          aria-label={t('pagination.next')}
        >
          <span>{t('pagination.next')}</span>
          <Icon icon="chevron-right" className="size-6" />
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-6">
        <div className="flex items-center gap-3">
          <span className="text-base text-unilink-text-primary">{t('pagination.itemsPerPage')}</span>
          <select
            value={pageSize}
            onChange={e => onPageSizeChange(Number(e.target.value))}
            aria-label={t('pagination.itemsPerPage')}
            className={PAGE_SIZE_SELECT_CLASS}
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </select>
        </div>

        <p className="text-base text-unilink-text-primary">
          <span>{t('pagination.showingResultsLeading')}</span>{' '}
          <span className="font-semibold">{startItem}</span>{' '}
          <span>{t('pagination.showingResultsTo')}</span>{' '}
          <span className="font-semibold">{endItem}</span>{' '}
          <span>{t('pagination.showingResultsOf')}</span>{' '}
          <span className="font-semibold">{totalItems}</span>{' '}
          <span>{t('pagination.showingResultsTrailing')}</span>
        </p>
      </div>
    </div>
  );
}
